import { Badge } from '@/components/ui/badge';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import type { ContainerStatus } from '../types';

interface ContainerStatusBadgeProps {
  status: ContainerStatus;
}

const statusVariant: Partial<
  Record<ContainerStatus, 'default' | 'secondary' | 'destructive' | 'outline'>
> = {
  at_risk: 'destructive',
  pending_assignment: 'secondary',
  waiting: 'secondary',
  overdue: 'outline'
};

const statusClassName: Partial<Record<ContainerStatus, string>> = {
  on_track:
    'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200',
  assigned:
    'bg-teal-100 text-teal-800 hover:bg-teal-100 dark:bg-teal-900 dark:text-teal-200',
  in_progress:
    'bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-200',
  pending_qa:
    'bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200',
  completed:
    'bg-gray-100 text-gray-800 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200',
  waiting:
    'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-200'
};

const statusTranslationKeys: Record<ContainerStatus, string> = {
  at_risk: 'containerStatusAtRisk',
  on_track: 'containerStatusOnTrack',
  overdue: 'containerStatusOverdue',
  pending_assignment: 'containerStatusPending',
  waiting: 'containerStatusWaiting',
  assigned: 'containerStatusAssigned',
  in_progress: 'containerStatusInProgress',
  pending_qa: 'containerStatusPendingQa',
  completed: 'containerStatusCompleted'
};

export function ContainerStatusBadge({ status }: ContainerStatusBadgeProps) {
  const t = useTranslations('handoverScanning');

  const variant = statusVariant[status] ?? 'secondary';
  const className = statusClassName[status];
  const translationKey = statusTranslationKeys[status];

  return (
    <Badge variant={variant} className={cn(className)}>
      {translationKey ? t(translationKey) : status}
    </Badge>
  );
}
